/**
 * ShadowedLightVolume — participating media that sample LIGHT VISIBILITY
 * (shader additions wave, 2026-09-15): sun shafts through buildings and trees,
 * lamp cones in fog, a headlight through dust. A local box volume is
 * ray-marched per pixel; at every march point the scene's lights are
 * evaluated WITH their shadow maps (the sun's CSM included) and a
 * Henyey–Greenstein phase, and the segment is integrated exactly.
 *
 * WHAT three ALREADY HAD, AND WHY THIS SUBCLASSES IT. r185's
 * `VolumeNodeMaterial` + `VolumetricLightingModel` already march a volume and
 * evaluate the standard lights loop at each step with
 * `context.shadowPositionWorld` set to the march point — which is exactly how
 * a light's `ShadowNode` (CSM too) gets sampled off the surface. Two things it
 * cannot do for us: its `direct()` SKIPS every light whose `distance` is
 * undefined — i.e. the SUN, the one shadow-caster this world has — and it has
 * no phase function. `ShadowedVolumeLightingModel` keeps the same march and
 * admits directionals with HG phase. It also does NOT multiply by
 * `lightNode.shadowNode` a second time: `AnalyticLightNode.setupShadow` has
 * already folded the shadow into the `lightColor` it hands `direct()`.
 *
 * THE INTEGRAL (per segment of length ds, extinction σt, source q):
 *   T_seg = e^(−σt·ds)          ΔC = T_before · q · (1 − T_seg) / σt
 *   T_after = T_before · T_seg   (limit → ds as σt → 0)
 * exact for constant coefficients along the segment.
 *   q = σs · ( Σ_lights lightColor·shadow·p(µ) · tint  +  ambient )
 *   p(µ) = (1 − g²) / (4π (1 + g² − 2gµ)^{3/2}),   µ = L·D
 * with L toward the light and D the camera ray (away from the camera): g > 0
 * scatters forward, so the sun in front of you glows through the haze.
 *
 * COMPOSITION. The material writes PREMULTIPLIED scattering in rgb and
 * (1 − T) in alpha (`setupOutput`), blended Normal — so the background is
 * attenuated by the medium's own transmittance. The march runs from the box
 * ENTRY along the camera ray to the box exit (this fragment: BackSide, no
 * depth test) or to the opaque scene depth, whichever is nearer — never
 * outside the box, never behind geometry.
 *
 * FRAME. `positionWorld` / `cameraPosition` here are RENDER space (the FP64
 * rebase: scene.position = −origin), so the box bounds the shader is handed
 * must be render space too: `handle.setBoxWorld(center, half)` with the mesh's
 * world position, not its absolute one. Atlas does that per frame.
 *
 * SCOPE. Shadow maps give geometric visibility; the medium does not shadow
 * ITSELF along the light path (lightT = 1) — a first tier, stated as such.
 * Rect-area lights are not scattered (three's LTC volume term is private).
 *
 * v2 (2026-09-25, Atlas realism §8 R7 — crepuscular rays):
 *   - CLOUD SHADOW on the sun: the DIRECTIONAL light's term is multiplied per
 *     step by the cloud transmittance at the point where the ray toward the sun
 *     crosses the cloud base (`p + L·(H − p.y)/L.y`), read from the cloud
 *     field's own coverage map with the same addressing as the ground's cloud
 *     shadows — shafts through the gaps of a broken deck, none under a storm.
 *   - The medium shades itself along the light path: a TWO-TAP extinction
 *     toward each light (`lightTaps`, e.g. 3 m and 10 m), replacing lightT = 1.
 *   - An EMISSION hook (`emissionNode`): a per-step emitted radiance E(p) the
 *     march integrates like the in-scatter, ΔC += T·E·(1 − e^(−σt·ds))/σt —
 *     a fire / plume volume reuses this model by passing its own E (e.g. a
 *     blackbody ramp of a temperature field). Unset = nothing added.
 *   Half-res + joint-bilateral upsampling stays a documented v3.
 *
 * v3 (2026-09-30, shader gaps program round 3, agent F — LOCAL VOLUMES). Every v3 option is OFF by default: a caller
 * that passes none of them gets the v2 material bit for bit (`legacyStart`, the v2 march verbatim).
 *
 *   THE COMPOSITE BUG (`composite: 'correct'`). v2 hands `super.setupOutput` an ALREADY-premultiplied vec4(C, 1 − T)
 *   with `premultipliedAlpha = true` — three's NodeMaterial.setupOutput multiplies rgb by alpha ITSELF (THE ONE
 *   PREMULTIPLY law), so the scattered light came out C·(1 − T): a thin haze (T 0.85) showed 15 % of its in-scatter.
 *   It also left `fog` on (three mixed the volume's own colour toward the scene fog colour) and carried no MRT overlay
 *   mark (the box's inner back face REPLACED the scene pass's normal / velocity / metal-rough targets under every
 *   pixel it covered — GTAO and SSR read a box). 'correct': CustomBlending (One, 1 − α) with the flag off, fog off,
 *   `markMrtOverlay`.
 *
 *   ONE MEDIUM AROUND LOCAL MEDIA (`splitBoxes`). A fire drawn after the volume was composited over ALL of it — never
 *   attenuated by the haze in front of it, while its soot dimmed the haze in front of it too. With split boxes the
 *   march is cut per pixel at the NEAREST box entry t_s along the ray (`setSplitBoxes`, render space): a BACK layer
 *   [max(t_entry, t_s), t_end] drawn where v2 drew (renderOrder 1, before the local media) and a FRONT layer
 *   [t_entry, min(t_s, t_end)] drawn after them (`frontRenderOrder`). Disjoint segments compose exactly —
 *   C = C_f + T_f·C_b, T = T_f·T_b — so a pixel with no box sees the old single integral, and where the haze and the
 *   fire overlap inside the fire's box their extinctions multiply (extinction adds). No temporal history: a fire that
 *   appears is composited correctly on its first frame.
 *
 *   HALF RESOLUTION (`resolution: 'half'`). The march runs at ⌈W/2⌉ × ⌈H/2⌉ of the render target it is drawn into
 *   (the scene pass's own size — correct under any upscale, THE AO-GRID LAW) in a NESTED render fired from the
 *   composite mesh's `onBeforeRender`: the current depth is copied (`copyFramebufferToTexture`), a private scene
 *   holding the march box renders into a RGBA16F target (C, T) with the MAIN scene's lights node
 *   (`material.lightsNode`, bound by `attach`), once per layer. A full-resolution composite then reconstructs each
 *   pixel from the four nearest half-res samples: a sample counts when its representative depth (the full texel
 *   (2i+1, 2j+1) the march used) continues the pixel's own surface — both CLAMPED at the box exit, with a slope
 *   allowance from the pixel's one-sided depth differences (a grazing floor is continuous, a silhouette is not);
 *   the accepted samples blend by their bilinear weights (Kopf 2007's joint bilateral with a binary range term;
 *   Jansen & Bavoil 2011's nearest-depth rule for a partial neighbourhood). When no sample continues the surface
 *   (a thin pole, a silhouette's inner pixel), `edgeFallback: 'march'` marches THAT pixel at full resolution
 *   (Shopf 2009's re-shade; needs Chrome's `diagnostic(off, derivative_uniformity)` — three emits it — for shadow
 *   lookups inside the branch; Firefox's WebGPU refuses that, so use 'nearest' there).
 *
 *   JITTER (`jitter`). The march's per-pixel start offset is `jitterNode(u.jitterMode, 1)` (bluenoise/
 *   SpatiotemporalBlueNoise): 'white' (v2's hash), 'golden', 'ign', 'stbn' — a uniform, switchable live.
 *
 *   RECT-AREA LIGHTS (`rectSamples` 1 | 4). v2's model had no `directRectArea`, so the window lights never scattered.
 *   Each RectAreaLight is sampled at its centre (1) or a 2×2 grid (4) as small patches: ΔE = L·(A/n)·cos θe / r²
 *   (one-sided: the light's −Z face), × the HG phase toward the rectangle's centre — the medium between a lit window
 *   and the viewer glows. An APPROXIMATION: unshadowed (the pool's lights are), no LTC, r² floored at the patch area.
 */
import * as THREE from 'three/webgpu';
import {
  float, int, ivec2, vec2, vec3, vec4, uniform, property, Loop, If, select, min, abs, floor,
  positionWorld, cameraPosition, cameraViewMatrix, cameraNear, cameraFar,
  perspectiveDepthToViewZ, viewportDepthTexture, screenUV, screenCoordinate, mx_noise_float, hash, time, texture,
} from 'three/tsl';
import { markMrtOverlay } from '../post/mrtOverlay.js';
import { jitterNode, jitterModeCode, type JitterMode } from '../bluenoise/SpatiotemporalBlueNoise.js';

export type VolumeComposite = 'legacy' | 'correct';
export type VolumeResolution = 'full' | 'half';
export type VolumeEdgeFallback = 'march' | 'nearest';
/** A render-space axis-aligned box the march is split around (a fire, a smoke column). */
export interface VolumeSplitBox { center: { x: number; y: number; z: number }; half: { x: number; y: number; z: number } }
/** The most boxes a pixel's ray is tested against (the split slots). */
export const MAX_SPLIT_BOXES = 4;

export interface ShadowedLightVolumeConfig {
  /** Box extents in metres (default 60 × 24 × 60). */
  size?: [number, number, number];
  /** Extinction σt per metre at full density (default 0.02 ≈ 200 m visibility). */
  density?: number;
  /** Scattering fraction σs/σt (default 0.9). */
  albedo?: number;
  /** HG anisotropy, −1..1 (default 0.55, forward). */
  g?: number;
  /** Isotropic sky in-scatter (radiance), default a faint blue. */
  ambient?: [number, number, number];
  /** Tint on the scattered light (default white). */
  color?: [number, number, number];
  /** Density e-folds every this many metres above the box floor (default 12). */
  heightFalloffM?: number;
  /** Metres per noise period (default 9) and how much of the density is noise, 0..1 (default 0.55). */
  noiseScaleM?: number;
  noiseAmount?: number;
  /** March steps per pixel (default 24). */
  steps?: number;
  /** Stop the march at the opaque scene depth (default true). */
  useSceneDepth?: boolean;
  /**
   * v2 cloud shadow on the SUN (R7): the cloud field's coverage map (R channel,
   * tileable), metres per repeat, the projection's rotation about world up,
   * and the coverage → shade threshold / softness (the ground's own rule —
   * CloudShadowLayer). The drift `cloudOffset`, `cloudStrength` and the cloud
   * base height (render-space Y) are uniforms the caller drives.
   */
  cloudShadow?: { texture: THREE.Texture; worldScale?: number; rotation?: number; threshold?: number; softness?: number };
  /** v2: two sample distances (m) toward each light for the medium's own shadow; omit for lightT = 1. */
  lightTaps?: [number, number];
  /**
   * v2 EMISSION HOOK: per-step emitted radiance at a RENDER-space point (a vec3
   * node factory). Integrated as ΔC += T·E·(1 − e^(−σt·ds))/σt.
   */
  emissionNode?: (p: unknown) => unknown;
  // ── v3 (2026-09-30) ──
  /** 'legacy' (default): the v2 output (premultipliedAlpha on (C, 1−T), scene fog, no MRT mark). 'correct': see the header. */
  composite?: VolumeComposite;
  /** 'full' (default) or 'half' (implies composite 'correct'; call `attach(renderer, scene)` before the first frame). */
  resolution?: VolumeResolution;
  /** The initial jitter sequence (v3 paths only; legacy keeps v2's hash). Default 'white'. */
  jitter?: JitterMode;
  /** Split slots (0..MAX_SPLIT_BOXES, default 0 = none) — v3 paths only. */
  splitBoxes?: number;
  /** renderOrder of the FRONT layer (after the local media it wraps). Default 9.5. */
  frontRenderOrder?: number;
  /** Rect-area lights scattered in the medium: 0 (default, none), 1 (the centre) or 4 (a 2×2 grid). v3 paths only. */
  rectSamples?: 0 | 1 | 4;
  /** half: the relative depth continuity tolerance (default 0.06) … */
  edgeTolerance?: number;
  /** … and what a pixel with no continuing sample does: 'march' (default) or 'nearest'. */
  edgeFallback?: VolumeEdgeFallback;
}

export interface ShadowedLightVolumeUniforms {
  density: { value: number };
  albedo: { value: number };
  g: { value: number };
  ambient: { value: THREE.Vector3 };
  color: { value: THREE.Vector3 };
  heightFalloff: { value: number };
  noiseScale: { value: number };
  noiseAmount: { value: number };
  wind: { value: THREE.Vector3 };
  boxMin: { value: THREE.Vector3 };
  boxMax: { value: THREE.Vector3 };
  steps: { value: number };
  /** v2 cloud shadow: world drift (render XZ + offset addresses the map), 0..1 strength, the base's render-space Y. */
  cloudOffset: { value: THREE.Vector2 };
  cloudStrength: { value: number };
  cloudBaseY: { value: number };
  // ── v3 ──
  /** The jitter sequence (JITTER_MODE_CODE: 0 white, 1 golden, 2 ign, 3 stbn). */
  jitterMode: { value: number };
  /** Active split boxes (≤ the slot count). */
  splitCount: { value: number };
  /** half: the continuity tolerance (relative). */
  edgeTolerance: { value: number };
  /** Debug view: 0 normal, 1 = the composite paints its edge pixels red (the fallback's share). */
  debugView: { value: number };
}

/** What a v3 handle did (the Atlas lever reads it). */
export interface ShadowedLightVolumeStats {
  mode: string;
  /** half: the last half-res target size. */
  half: [number, number];
  /** half: nested marches issued (one per layer per frame). */
  marches: number;
  /** half: composite draws that marched nothing (not the primary camera, no render target, a compile sweep). */
  skipped: number;
  /** Split boxes active now. */
  splitActive: number;
  /** half: private march-scene warms issued (compileAsync before the first nested draw, again after a lights change). */
  warms?: number;
}

export interface ShadowedLightVolumeHandle {
  /** Add this to the scene (in split modes it carries the FRONT layer as a child). */
  mesh: THREE.Mesh;
  material: ShadowedVolumeMaterial;
  uniforms: ShadowedLightVolumeUniforms;
  /** Render-space box (centre + half extents); call after moving the mesh. */
  setBoxWorld(center: THREE.Vector3, half: THREE.Vector3): void;
  /** v3: the render-space boxes the march is split around (at most the slot count; [] = none). */
  setSplitBoxes(boxes: readonly VolumeSplitBox[]): void;
  /** v3 half: the renderer and the MAIN scene whose lights the nested march uses; the camera whose frames it marches (others draw nothing). */
  attach(renderer: THREE.WebGPURenderer, scene: THREE.Scene, camera?: THREE.Camera | null): void;
  readonly stats: ShadowedLightVolumeStats;
  /** v3 half: resolves once the private march pipeline is warm (THE WARM); at once for every other mode. A caller that
   *  measures the first drawn frame renders once (which queues the warm) and awaits this. */
  whenWarm?(): Promise<void>;
  dispose(): void;
}

// ── CPU mirrors (the harness pins these) ──────────────────────────────────

/** Henyey–Greenstein phase for µ = L·D (L toward the light, D the camera ray). */
export function hgPhaseCpu(mu: number, g: number): number {
  const gg = g * g;
  const d = 1 + gg - 2 * g * Math.max(-1, Math.min(1, mu));
  return (1 - gg) / (4 * Math.PI * Math.pow(Math.max(1e-6, d), 1.5));
}

/** One segment of the front-to-back integral: returns the added radiance and the transmittance after it. */
export function segmentScatterCpu(sigmaT: number, ds: number, q: number, tBefore: number): { dC: number; tAfter: number } {
  const st = Math.max(0, sigmaT);
  const tSeg = Math.exp(-st * ds);
  const integral = st < 1e-6 ? ds : (1 - tSeg) / st;
  return { dC: tBefore * q * integral, tAfter: tBefore * tSeg };
}

/** Height density factor: 1 at and below the floor, e-folding every `falloffM` above it. */
export function heightDensityCpu(y: number, floorY: number, falloffM: number): number {
  return Math.exp(-Math.max(0, y - floorY) / Math.max(1e-3, falloffM));
}

/** v3: two disjoint segments front-to-back — the composition law C = C_f + T_f·C_b, T = T_f·T_b. */
export function composeLayersCpu(front: { C: number; T: number }, back: { C: number; T: number }): { C: number; T: number } {
  return { C: front.C + front.T * back.C, T: front.T * back.T };
}

/** v3: the ray's entry distance into a box, or +∞ when it misses (or the box lies wholly behind t0). */
export function rayBoxEntryCpu(o: { x: number; y: number; z: number }, d: { x: number; y: number; z: number }, box: VolumeSplitBox, t0 = 0): number {
  let tn = -Infinity, tf = Infinity;
  for (const k of ['x', 'y', 'z'] as const) {
    const dk = Math.abs(d[k]) > 1e-5 ? d[k] : (d[k] < 0 ? -1e-5 : 1e-5);
    const a = (box.center[k] - box.half[k] - o[k]) / dk, b = (box.center[k] + box.half[k] - o[k]) / dk;
    tn = Math.max(tn, Math.min(a, b)); tf = Math.min(tf, Math.max(a, b));
  }
  return tf > Math.max(tn, t0) ? Math.max(tn, t0) : Infinity;
}

/** v3: the split distance for a ray — the nearest box entry inside [tEntry, tEnd], else tEntry (no split: all back). */
export function splitDistanceCpu(o: { x: number; y: number; z: number }, d: { x: number; y: number; z: number }, boxes: readonly VolumeSplitBox[], tEntry: number, tEnd: number): number {
  let ts = Infinity;
  for (const b of boxes) ts = Math.min(ts, rayBoxEntryCpu(o, d, b, tEntry));
  return ts < tEnd ? Math.min(Math.max(ts, tEntry), tEnd) : tEntry;
}

/**
 * v3 half: the composite's reconstruction rule for one full-res pixel (the TSL mirrors it exactly). `tPixel` is the
 * pixel's own march-end distance, `tSamples` the four neighbours' (both already clamped at the box exit), `bil` their
 * bilinear weights, `allow` the absolute allowances (tolerance·t + the slope term). Returns the weights (sum 1) or
 * `edge: true` when no neighbour continues the surface.
 */
export function upsampleWeightsCpu(tPixel: number, tSamples: readonly number[], bil: readonly number[], allow: readonly number[]): { weights: number[]; edge: boolean } {
  const acc = tSamples.map((t, k) => (Math.abs(t - tPixel) <= allow[k] ? bil[k] : 0));
  const s = acc.reduce((a, b) => a + b, 0);
  if (s > 1e-5) return { weights: acc.map((w) => w / s), edge: false };
  return { weights: [0, 0, 0, 0], edge: true };
}

/**
 * v3 half-res: the slope term along one axis — the smaller one-sided difference when the two AGREE in sign (a monotone
 * surface), 0 at a local extremum (a feature one or two pixels thin). The composite's allowance per neighbour is
 * `tP·tolerance + 1.5·(gx·dx + gy·dy) + 0.02` with these slopes.
 */
export function slopeTermCpu(tMinus: number, tP: number, tPlus: number): number {
  const dF = tPlus - tP, dB = tP - tMinus;
  return dF * dB > 0 ? Math.min(Math.abs(dF), Math.abs(dB)) : 0;
}

/** v3: the in-scatter irradiance of one rect-area patch at a point (the TSL term before the phase). */
export function rectPatchIrradianceCpu(L: number, areaPerSample: number, cosEmit: number, r2: number): number {
  return L * areaPerSample * Math.max(0, cosEmit) / Math.max(r2, areaPerSample);
}

// ── The lighting model ────────────────────────────────────────────────────

const scatteredLight = property('vec3', 'volumeScatteredLight');
/** The render-space march point direct() reads (v2: the cloud lookup and the light-path taps). */
const marchPoint = property('vec3', 'volumeMarchPoint');
const outgoingRayLight = property('vec3', 'volumeOutgoingLight');
const rayTransmittance = property('float', 'volumeTransmittance');

interface ModelUniforms {
  g: ReturnType<typeof uniform>;
}

/** CPU mirror of the v2 sun-path cloud lookup: where a ray from `p` toward the sun meets the cloud base. */
export function cloudBaseHitCpu(p: { x: number; y: number; z: number }, L: { x: number; y: number; z: number }, baseY: number): { x: number; z: number } | null {
  if (L.y <= 1e-3) return null;
  const t = (baseY - p.y) / L.y;
  return { x: p.x + L.x * t, z: p.z + L.z * t };
}
/** CPU mirror of the cloud shade rule (CloudShadowLayer's): 1 = lit. */
export function cloudLitCpu(coverage: number, strength: number, threshold: number, softness: number): number {
  const t = Math.max(0, Math.min(1, (coverage - threshold) / Math.max(1e-6, softness)));
  return 1 - t * t * (3 - 2 * t) * strength;
}
/** CPU mirror of the two-tap light-path transmittance (σ in 1/m at the two taps, tap distances in m). */
export function lightPathTransmittanceCpu(sigma1: number, sigma2: number, d1: number, d2: number): number {
  return Math.exp(-(sigma1 * d1 + sigma2 * Math.max(0, d2 - d1)));
}

type N = any;

/**
 * v3 march / composite options for one material (fixed at build). `kind`: 'legacy' (v2 verbatim), 'march' (integrate
 * a layer's range and write (C, T)), 'composite' (reconstruct from the half-res targets; optionally march an edge pixel).
 */
export interface VolumeModelOptions {
  kind: 'legacy' | 'march' | 'composite';
  /** 'all' (no split), 'back' / 'front' (fixed), or 'uniform' (the half march: `layerUniform` picks, 0 back, 1 front). */
  layer: 'all' | 'back' | 'front' | 'uniform';
  layerUniform?: N;
  /** Opaque hardware depth for this fragment; undefined = `viewportDepthTexture(screenUV)` (v2). */
  depthAt?: () => N;
  /** The split slots: box min/max uniforms and the active count. */
  split?: { mins: N[]; maxs: N[]; count: N };
  /** The composite's inputs. */
  upsample?: {
    /** `depth`: this frame's depth (the pixel's own surface). `depthUsed`: the depth the half march read (see THE
     *  QUEUE-ORDER LAW in the half path — the previous frame's on WebGPU, this frame's on WebGL 2). */
    half: THREE.Texture; depth: THREE.Texture; depthUsed: THREE.Texture; fullSize: N; halfSize: N; tolerance: N;
    fallback: VolumeEdgeFallback; debug: N; skip: N;
  };
  rectSamples: 0 | 1 | 4;
}

export class ShadowedVolumeLightingModel extends THREE.LightingModel {
  private readonly opts: VolumeModelOptions;
  constructor(private readonly u: ModelUniforms, private readonly densityAt: (p: unknown) => unknown, private readonly vol: {
    density: ReturnType<typeof uniform>; albedo: ReturnType<typeof uniform>; ambient: ReturnType<typeof uniform>;
    color: ReturnType<typeof uniform>; boxMin: ReturnType<typeof uniform>; boxMax: ReturnType<typeof uniform>;
    steps: ReturnType<typeof uniform>; useSceneDepth: boolean;
    /** v2: the sun's cloud transmittance at a render-space point (1 = lit), the light-path taps, the emission. */
    sunCloudLit?: (p: unknown) => unknown;
    lightTaps?: [number, number];
    emission?: (p: unknown) => unknown;
    /** v3: the density the light taps read (the mean medium — THE TAP LAW); legacy keeps `densityAt`. */
    tapDensityAt?: (p: unknown) => unknown;
  }, opts?: VolumeModelOptions) {
    super();
    this.opts = opts ?? { kind: 'legacy', layer: 'all', rectSamples: 0 };
  }

  /**
   * The lights node that owns this build (THE DEFERRED-LOOP LIGHTS LAW): `LightsNode.setup` sets `builder.lightsNode` to
   * itself only while `start()` runs and restores the RENDER OBJECT's node when it returns — but a TSL `Loop` body is
   * built after that, so `super.start` inside the march loop read the render object's node. For the main scene the two
   * are the same node; for the nested half march (a private scene, the main scene's lights through
   * `material.lightsNode`) the render object's node is the private scene's EMPTY one — the half march saw no light at
   * all (measured: C unchanged by the sun ×10 or a new point light). Captured here, re-seated around `super.start`.
   */
  private lightsOwner: any = null;

  private startLights(builder: any): void {
    const prev = builder.lightsNode;
    builder.lightsNode = this.lightsOwner ?? prev;
    super.start(builder);
    builder.lightsNode = prev;
  }

  start(builder: any): void {
    this.lightsOwner = builder.lightsNode;
    if (this.opts.kind === 'legacy') { this.legacyStart(builder); return; }
    if (this.opts.kind === 'march') { this.v3March(builder); return; }
    this.v3Composite(builder);
  }

  /** v2's march, VERBATIM (the default path — `composite: 'legacy'`, full resolution, no split, no rect lights). */
  private legacyStart(builder: any): void {
    const { material } = builder;
    const cam = cameraPosition;
    const toFrag = positionWorld.sub(cam);
    const fragDist = toFrag.length().toVar();
    const rayDir = toFrag.div(fragDist.max(1e-4)).toVar();
    // Slab test against the render-space box: march only INSIDE it.
    const inv = vec3(1).div(rayDir.abs().max(1e-5)).mul(rayDir.sign());
    const t0 = this.vol.boxMin.sub(cam).mul(inv), t1 = this.vol.boxMax.sub(cam).mul(inv);
    const tNear = t0.min(t1), tFar = t0.max(t1);
    const tEntry = tNear.x.max(tNear.y).max(tNear.z).max(0).toVar();
    let tExit = tFar.x.min(tFar.y).min(tFar.z).min(fragDist).toVar();
    if (this.vol.useSceneDepth) {
      // The opaque depth at this pixel as a distance along OUR ray.
      const depth = viewportDepthTexture(screenUV).r;
      const viewZ = perspectiveDepthToViewZ(depth, cameraNear, cameraFar); // negative metres
      const dirView = cameraViewMatrix.mul(vec4(rayDir, float(0))).xyz;
      const tScene = viewZ.negate().div(dirView.z.negate().max(1e-4));
      tExit = tExit.min(tScene).toVar();
    }
    const span = tExit.sub(tEntry).max(0).toVar();
    const stepSize = span.div(float(this.vol.steps)).toVar();
    const dist = tEntry.toVar();
    if (material.offsetNode) dist.addAssign(material.offsetNode.mul(stepSize)); // jitter against banding
    const T = float(1).toVar();
    outgoingRayLight.assign(vec3(0));

    Loop(this.vol.steps, () => {
      const p = cam.add(rayDir.mul(dist));
      const pView = cameraViewMatrix.mul(vec4(p, 1)).xyz;
      builder.context.positionWorld = p;
      builder.context.shadowPositionWorld = p;
      builder.context.positionView = pView;
      scatteredLight.assign(vec3(0));
      marchPoint.assign(p);
      // Every light, shadow-sampled at THIS point, into `scatteredLight` (direct()).
      super.start(builder);
      const sigmaT = this.vol.density.mul(float(this.densityAt(p) as never)).toVar();
      const sigmaS = sigmaT.mul(this.vol.albedo);
      const q = sigmaS.mul(scatteredLight.mul(this.vol.color).add(this.vol.ambient));
      const tSeg = sigmaT.negate().mul(stepSize).exp();
      const integral = select(sigmaT.lessThan(1e-5), stepSize, float(1).sub(tSeg).div(sigmaT.max(1e-5)));
      outgoingRayLight.addAssign(q.mul(T).mul(integral));
      // v2 EMISSION HOOK: an emitting medium (a fire) — the same exact segment integral.
      if (this.vol.emission) outgoingRayLight.addAssign(vec3(this.vol.emission(p) as never).mul(T).mul(integral));
      T.mulAssign(tSeg);
      dist.addAssign(stepSize);
    });
    rayTransmittance.assign(T);
  }

  // ── v3 ──

  /** The ray, the box range and the opaque end for this fragment (render space). */
  private v3Ray(): { cam: N; rayDir: N; tEntry: N; tBoxExit: N; tEnd: N; dirViewZ: N } {
    const cam = cameraPosition;
    const toFrag = positionWorld.sub(cam);
    const fragDist = toFrag.length().toVar();
    const rayDir = toFrag.div(fragDist.max(1e-4)).toVar();
    const inv = vec3(1).div(rayDir.abs().max(1e-5)).mul(rayDir.sign());
    const t0 = this.vol.boxMin.sub(cam).mul(inv), t1 = this.vol.boxMax.sub(cam).mul(inv);
    const tNear = t0.min(t1), tFar = t0.max(t1);
    const tEntry = tNear.x.max(tNear.y).max(tNear.z).max(0).toVar();
    const tBoxExit = tFar.x.min(tFar.y).min(tFar.z).min(fragDist).toVar();
    const dirViewZ = cameraViewMatrix.mul(vec4(rayDir, float(0))).z.negate().max(1e-4).toVar();
    let tEnd: N = tBoxExit;
    if (this.vol.useSceneDepth) {
      // THE LAZY-SAMPLE LAW: the depth sample is placed here, at the top, before any branch reads it.
      const depth = float(this.opts.depthAt ? this.opts.depthAt() : viewportDepthTexture(screenUV).r).toVar();
      const viewZ = perspectiveDepthToViewZ(depth, cameraNear, cameraFar);
      tEnd = tBoxExit.min(viewZ.negate().div(dirViewZ)).toVar();
    }
    return { cam, rayDir, tEntry, tBoxExit, tEnd, dirViewZ };
  }

  /** The split distance for this ray: the nearest active box entry in [tEntry, tEnd], else tEntry. */
  private v3Split(cam: N, rayDir: N, tEntry: N, tEnd: N): N {
    const sp = this.opts.split;
    if (!sp) return tEntry;
    const inv = vec3(1).div(rayDir.abs().max(1e-5)).mul(rayDir.sign()).toVar();
    const ts = float(1e9).toVar();
    for (let k = 0; k < sp.mins.length; k++) {
      const a = vec3(sp.mins[k]).sub(cam).mul(inv), b = vec3(sp.maxs[k]).sub(cam).mul(inv);
      const tn = a.min(b), tf = a.max(b);
      const tin = tn.x.max(tn.y).max(tn.z).max(tEntry);
      const tout = tf.x.min(tf.y).min(tf.z);
      const hit = tout.greaterThan(tin).and(int(k).lessThan(sp.count));
      ts.assign(select(hit, ts.min(tin), ts));
    }
    return select(ts.lessThan(tEnd), ts.max(tEntry).min(tEnd), tEntry);
  }

  /** The v3 march of [ta, tb]: every light (with rect patches), the jitter, the exact segment integral → (C, T) vars. */
  private v3MarchRange(builder: any, cam: N, rayDir: N, ta: N, tb: N): { C: N; T: N } {
    const { material } = builder;
    const span = tb.sub(ta).max(0).toVar();
    const stepSize = span.div(float(this.vol.steps)).toVar();
    const dist = ta.toVar();
    if (material.offsetNode) dist.addAssign(float(material.offsetNode).mul(stepSize));
    const T = float(1).toVar();
    const C = vec3(0).toVar();
    Loop(this.vol.steps, () => {
      const p = cam.add(rayDir.mul(dist));
      const pView = cameraViewMatrix.mul(vec4(p, 1)).xyz;
      builder.context.positionWorld = p;
      builder.context.shadowPositionWorld = p;
      builder.context.positionView = pView;
      scatteredLight.assign(vec3(0));
      marchPoint.assign(p);
      this.startLights(builder);
      const sigmaT = this.vol.density.mul(float(this.densityAt(p) as never)).toVar();
      const sigmaS = sigmaT.mul(this.vol.albedo);
      const q = sigmaS.mul(scatteredLight.mul(this.vol.color).add(this.vol.ambient));
      const tSeg = sigmaT.negate().mul(stepSize).exp();
      const integral = select(sigmaT.lessThan(1e-5), stepSize, float(1).sub(tSeg).div(sigmaT.max(1e-5)));
      C.addAssign(q.mul(T).mul(integral));
      if (this.vol.emission) C.addAssign(vec3(this.vol.emission(p) as never).mul(T).mul(integral));
      T.mulAssign(tSeg);
      dist.addAssign(stepSize);
    });
    return { C, T };
  }

  /** The layer's range from the split: all [tEntry, tEnd]; back [ts, tEnd]; front [tEntry, ts]. */
  private v3Range(layer: 'all' | 'back' | 'front' | 'uniform', tEntry: N, ts: N, tEnd: N): [N, N] {
    if (layer === 'all') return [tEntry, tEnd];
    if (layer === 'back') return [ts, tEnd];
    if (layer === 'front') return [tEntry, ts];
    const isFront = float(this.opts.layerUniform).greaterThan(0.5);
    return [select(isFront, tEntry, ts), select(isFront, ts, tEnd)];
  }

  private v3March(builder: any): void {
    const { cam, rayDir, tEntry, tEnd } = this.v3Ray();
    const ts = this.v3Split(cam, rayDir, tEntry, tEnd).toVar();
    const [ta, tb] = this.v3Range(this.opts.layer, tEntry, ts, tEnd);
    const r = this.v3MarchRange(builder, cam, rayDir, ta, tb);
    outgoingRayLight.assign(r.C);
    rayTransmittance.assign(r.T);
  }

  /**
   * The half-res composite (see the header): the four nearest half texels, each counted when its representative depth
   * continues this pixel's surface (clamped at the box exit, slope allowance), blended bilinearly among the accepted;
   * none accepted → an edge: march at full resolution ('march') or take the nearest-depth sample ('nearest').
   */
  private v3Composite(builder: any): void {
    const up = this.opts.upsample!;
    const { cam, rayDir, tEntry, tBoxExit, tEnd, dirViewZ } = this.v3Ray();
    const depthTex = up.depth;
    const full = vec2(up.fullSize), halfS = vec2(up.halfSize);
    const fullMax = ivec2(full.sub(1));
    const pf = floor(screenCoordinate.xy).toVar();
    // A texel's march-end distance along THIS ray (the neighbours' rays differ by < a pixel — their clamp is ours).
    const tOfIn = (tex: THREE.Texture, texel: N): N => {
      const d = texture(tex).load(ivec2(texel).clamp(ivec2(0), fullMax)).r;
      const vz = perspectiveDepthToViewZ(d, cameraNear, cameraFar).negate();
      return vz.div(dirViewZ).min(tBoxExit).max(tEntry);
    };
    const tOf = (texel: N): N => tOfIn(depthTex, texel);
    // A coarse texel is judged by the depth IT was marched with (a disocclusion since → rejected → the fallback).
    const tUsed = (texel: N): N => tOfIn(up.depthUsed, texel);
    const tP = tEnd.min(tBoxExit).max(tEntry).toVar();
    // One-sided slopes (the smaller of forward / backward differences — a silhouette on one side does not widen it),
    // and only where the two agree in sign: a surface is monotone across a pixel, a feature ONE OR TWO pixels thin (a
    // pole, a wire) is a local extremum — both differences large, opposite signs — and gets no slope allowance, or the
    // thin pole's own huge difference to the background would accept every coarse sample (measured: 0 edge pixels at
    // the bed's 1.7-px pole before this rule). `slopeTermCpu` is the mirror.
    const tXp = tOf(pf.add(vec2(1, 0))).toVar(), tXm = tOf(pf.sub(vec2(1, 0))).toVar();
    const tYp = tOf(pf.add(vec2(0, 1))).toVar(), tYm = tOf(pf.sub(vec2(0, 1))).toVar();
    const slope = (tPlus: N, tMinus: N): N => {
      const dF = tPlus.sub(tP), dB = tP.sub(tMinus);
      return select(dF.mul(dB).greaterThan(0), min(abs(dF), abs(dB)), float(0));
    };
    const gx = slope(tXp, tXm).toVar(), gy = slope(tYp, tYm).toVar();
    // Half-texel position of this pixel's centre, the 2×2 neighbourhood and its bilinear weights.
    const q = pf.add(0.5).mul(0.5).sub(0.5);
    const i0 = floor(q).toVar();
    const f = q.sub(i0).toVar();
    const hMax = vec2(ivec2(halfS.sub(1)));
    const acc = vec4(0).toVar();
    const wsum = float(0).toVar();
    const best = vec4(0, 0, 0, 1).toVar();
    const bestErr = float(1e9).toVar();
    const offs: Array<[number, number]> = [[0, 0], [1, 0], [0, 1], [1, 1]];
    for (const [ox, oy] of offs) {
      const ik = i0.add(vec2(ox, oy)).clamp(vec2(0), hMax).toVar();
      const rep = ik.mul(2).add(1); // the full texel the half march read its depth at
      const tK = tUsed(rep).toVar();
      const bil = (ox ? f.x : float(1).sub(f.x)).mul(oy ? f.y : float(1).sub(f.y));
      const dx = abs(rep.x.sub(pf.x)), dy = abs(rep.y.sub(pf.y));
      const allow = tP.mul(up.tolerance).add(gx.mul(dx).add(gy.mul(dy)).mul(1.5)).add(0.02);
      const err = abs(tK.sub(tP)).toVar();
      const v = texture(up.half).load(ivec2(ik)).toVar();
      const ok = err.lessThanEqual(allow);
      acc.addAssign(select(ok, v.mul(bil), vec4(0)));
      wsum.addAssign(select(ok, bil, float(0)));
      const better = err.lessThan(bestErr);
      best.assign(select(better, v, best));
      bestErr.assign(select(better, err, bestErr));
    }
    const edge = wsum.lessThan(1e-5).toVar();
    const res = select(edge, best, acc.div(wsum.max(1e-5))).toVar();
    outgoingRayLight.assign(res.xyz);
    rayTransmittance.assign(res.w);
    if (up.fallback === 'march') {
      If(edge.and(float(up.skip).lessThan(0.5)), () => {
        const ts = this.v3Split(cam, rayDir, tEntry, tEnd).toVar();
        const lay = this.opts.layer === 'front' ? 'front' : this.opts.layer === 'all' ? 'all' : 'back';
        const [ta, tb] = this.v3Range(lay, tEntry, ts, tEnd);
        const r = this.v3MarchRange(builder, cam, rayDir, ta, tb);
        outgoingRayLight.assign(r.C);
        rayTransmittance.assign(r.T);
      });
    }
    // Debug view 1: the edge pixels red (opaque), everything else untouched. 2: this pixel's copied depth (grey,
    // opaque). 3: the nearest half texel's in-scatter ×4. 4: the nearest half texel's transmittance (grey).
    If(float(up.debug).greaterThan(0.5), () => {
      outgoingRayLight.assign(select(edge, vec3(1, 0, 0), vec3(0)));
      rayTransmittance.assign(select(edge, float(0), float(1)));
    });
    If(float(up.debug).greaterThan(1.5), () => {
      const near = texture(up.half).load(ivec2(i0.add(f.round()).clamp(vec2(0), hMax)));
      const own = texture(depthTex).load(ivec2(pf).clamp(ivec2(0), fullMax)).r;
      const v = float(up.debug);
      outgoingRayLight.assign(select(v.lessThan(2.5), vec3(own), select(v.lessThan(3.5), near.xyz.mul(4), vec3(near.w))));
      rayTransmittance.assign(float(0));
    });
    // Not the primary camera (a reflection, a probe capture): draw nothing.
    If(float(up.skip).greaterThan(0.5), () => {
      outgoingRayLight.assign(vec3(0));
      rayTransmittance.assign(float(1));
    });
  }

  direct({ lightNode, lightColor, lightDirection }: any, builder: any): void {
    if (lightNode?.isAnalyticLightNode !== true) return;
    // `lightColor` already carries the light's shadow (AnalyticLightNode.setupShadow).
    const light = vec3(lightColor).toVar();
    const pw = marchPoint;
    // v2: the SUN through the clouds — a directional light's term × the cloud transmittance where
    // the path toward it crosses the cloud base (the ground's own cloud-shadow rule and map).
    if (this.vol.sunCloudLit && lightNode.light?.isDirectionalLight && lightDirection) {
      light.mulAssign(float(this.vol.sunCloudLit(pw) as never));
    }
    // v2: the medium's own shadow toward the light — two taps (lightT, replacing 1).
    if (this.vol.lightTaps && lightDirection) {
      const [d1, d2] = this.vol.lightTaps;
      const Lw = vec3(lightDirection).transformDirection(cameraViewMatrix.transpose()).normalize();
      const dens = this.opts.kind === 'legacy' || !this.vol.tapDensityAt ? this.densityAt : this.vol.tapDensityAt;
      const s1 = this.vol.density.mul(float(dens(pw.add(Lw.mul(d1))) as never));
      const s2 = this.vol.density.mul(float(dens(pw.add(Lw.mul(d2))) as never));
      light.mulAssign(s1.mul(d1).add(s2.mul(d2 - d1)).negate().exp());
    }
    if (lightDirection) {
      // µ = L·D with D the camera ray in view space (camera at the origin).
      const d = builder.context.positionView;
      const mu = vec3(lightDirection).dot(vec3(d).normalize());
      const g = this.u.g;
      const gg = g.mul(g);
      const den = float(1).add(gg).sub(g.mul(2).mul(mu)).max(1e-4);
      const phase = float(1).sub(gg).div(den.pow(1.5).mul(4 * Math.PI));
      light.mulAssign(phase);
    }
    scatteredLight.addAssign(light);
  }

  /**
   * v3 RECT-AREA LIGHTS (`rectSamples`): small emitting patches — ΔE = L·(A/n)·cos θe / max(r², A/n), one-sided
   * (the light's −Z face: lit where (P − q)·(hw × hh) < 0 — three's LTC_Evaluate_Volume convention), × the HG phase
   * toward the rectangle's centre. View space (three passes the light's position and half extents in view space).
   * An approximation: unshadowed, no LTC. v2's model has no directRectArea (the base LightingModel's is empty).
   */
  directRectArea({ lightColor, lightPosition, halfWidth, halfHeight }: any, builder: any): void {
    const n = this.opts.rectSamples;
    if (!n) return;
    const P = vec3(builder.context.positionView);
    const hw = vec3(halfWidth), hh = vec3(halfHeight), c = vec3(lightPosition);
    const nrmRaw = hw.cross(hh);
    const area = nrmRaw.length().mul(4); // |hw × hh| · 4 = width · height
    const nrm = nrmRaw.div(nrmRaw.length().max(1e-6));
    const aPer = area.div(n).max(1e-4);
    // Patch centres: the centre, or the four quarter points (±½ of each half extent).
    const pts: Array<[number, number]> = n === 1 ? [[0, 0]] : [[-0.5, -0.5], [0.5, -0.5], [-0.5, 0.5], [0.5, 0.5]];
    const E = float(0).toVar();
    for (const [a, b] of pts) {
      const qd = c.add(hw.mul(a)).add(hh.mul(b));
      const toP = P.sub(qd);
      const r2 = toP.dot(toP);
      const cosE = toP.dot(nrm).negate().div(r2.sqrt().max(1e-4)); // the emitting face's normal is −(hw × hh)
      E.addAssign(aPer.mul(cosE.max(0)).div(r2.max(aPer)));
    }
    // HG toward the rectangle's centre (µ = L·D: L from P toward the light, D the camera ray).
    const Lc = c.sub(P).normalize();
    const mu = Lc.dot(P.normalize());
    const g = this.u.g;
    const gg = g.mul(g);
    const den = float(1).add(gg).sub(g.mul(2).mul(mu)).max(1e-4);
    const phase = float(1).sub(gg).div(den.pow(1.5).mul(4 * Math.PI));
    scatteredLight.addAssign(vec3(lightColor).mul(E).mul(phase));
  }

  finish(builder: any): void {
    builder.context.outgoingLight.assign(outgoingRayLight);
  }
}

// ── The material ──────────────────────────────────────────────────────────

export class ShadowedVolumeMaterial extends THREE.VolumeNodeMaterial {
  model: ShadowedVolumeLightingModel | null = null;
  /** v3: 'premult' (the default — (C, 1 − T), v2's alpha), 'raw' (the half-res target: (C, T)). */
  volumeOutput: 'premult' | 'raw' = 'premult';
  setupLightingModel(): any { return this.model; }
  /** Premultiplied scattering + (1 − T) coverage, blended Normal (v2); v3 'raw' writes (C, T) for the half target. */
  setupOutput(builder: any, outputNode: any): any {
    if (this.volumeOutput === 'raw') return super.setupOutput(builder, vec4(vec3(outputNode.rgb), rayTransmittance));
    return super.setupOutput(builder, vec4(vec3(outputNode.rgb), float(1).sub(rayTransmittance)));
  }
}

/** v3 'correct' output state: CustomBlending (One, 1 − α) with premultipliedAlpha OFF, fog off, the MRT overlay mark. */
function applyCorrectComposite(m: ShadowedVolumeMaterial): void {
  m.premultipliedAlpha = false;
  m.fog = false;
  m.blending = THREE.CustomBlending;
  m.blendSrc = THREE.OneFactor;
  m.blendDst = THREE.OneMinusSrcAlphaFactor;
  m.blendSrcAlpha = THREE.OneFactor;
  m.blendDstAlpha = THREE.OneMinusSrcAlphaFactor;
  m.transparent = true;
  m.depthWrite = false;
  m.depthTest = false;
  m.side = THREE.BackSide;
  markMrtOverlay(m);
}

export function createShadowedLightVolume(config?: ShadowedLightVolumeConfig): ShadowedLightVolumeHandle {
  const size = config?.size ?? [60, 24, 60];
  const amb = config?.ambient ?? [0.03, 0.035, 0.045];
  const col = config?.color ?? [1, 1, 1];
  const u = {
    density: uniform(config?.density ?? 0.02),
    albedo: uniform(config?.albedo ?? 0.9),
    g: uniform(config?.g ?? 0.55),
    ambient: uniform(new THREE.Vector3(amb[0], amb[1], amb[2])),
    color: uniform(new THREE.Vector3(col[0], col[1], col[2])),
    heightFalloff: uniform(config?.heightFalloffM ?? 12),
    noiseScale: uniform(config?.noiseScaleM ?? 9),
    noiseAmount: uniform(config?.noiseAmount ?? 0.55),
    wind: uniform(new THREE.Vector3(0, 0, 0)),
    boxMin: uniform(new THREE.Vector3(-size[0] / 2, -size[1] / 2, -size[2] / 2)),
    boxMax: uniform(new THREE.Vector3(size[0] / 2, size[1] / 2, size[2] / 2)),
    steps: uniform(config?.steps ?? 24, 'int'),
    cloudOffset: uniform(new THREE.Vector2(0, 0)),
    cloudStrength: uniform(0),
    cloudBaseY: uniform(1500),
    // v3
    jitterMode: uniform(jitterModeCode(config?.jitter), 'int'),
    splitCount: uniform(0, 'int'),
    edgeTolerance: uniform(config?.edgeTolerance ?? 0.06),
    debugView: uniform(0, 'int'),
  };
  // v2 cloud shadow on the sun (R7): the ground's CloudShadowLayer addressing, at the base-plane hit toward the sun.
  let sunCloudLit: ((p: unknown) => unknown) | undefined;
  const cs = config?.cloudShadow;
  if (cs) {
    const cr = Math.cos(cs.rotation ?? 0), sr = Math.sin(cs.rotation ?? 0);
    const scale = cs.worldScale ?? 6000, th = cs.threshold ?? 0.4, soft = cs.softness ?? 0.3;
    const sunDirW = uniform(new THREE.Vector3(0, 1, 0));
    (u as unknown as { sunDir: unknown }).sunDir = sunDirW;
    sunCloudLit = (p: unknown): unknown => {
      const pp = vec3(p as never);
      const L = vec3(sunDirW as never).normalize();
      const t = u.cloudBaseY.sub(pp.y).div(L.y.max(0.02));
      const hit = pp.xz.add(L.xz.mul(t)).add(u.cloudOffset);
      const uvC = vec3(hit.x.mul(cr).sub(hit.y.mul(sr)), hit.x.mul(sr).add(hit.y.mul(cr)), 0).xy.div(scale);
      const cov = texture(cs.texture, uvC).r;
      const shade = cov.sub(th).div(soft).clamp(0, 1);
      return float(1).sub(shade.mul(shade).mul(float(3).sub(shade.mul(2))).mul(u.cloudStrength));
    };
  }
  // Density 0..1 at a render-space point: inside the box, falling off with
  // height above the box floor, broken up by drifting noise.
  const densityAt = (p: unknown): unknown => {
    const pp = vec3(p as never);
    const edge = float(1.5);
    const inX = pp.x.sub(u.boxMin.x).min(u.boxMax.x.sub(pp.x)).div(edge).clamp(0, 1);
    const inY = pp.y.sub(u.boxMin.y).min(u.boxMax.y.sub(pp.y)).div(edge).clamp(0, 1);
    const inZ = pp.z.sub(u.boxMin.z).min(u.boxMax.z.sub(pp.z)).div(edge).clamp(0, 1);
    const inside = inX.mul(inY).mul(inZ);
    const h = pp.y.sub(u.boxMin.y).max(0).div(u.heightFalloff).negate().exp();
    const n = mx_noise_float(pp.div(u.noiseScale).add(u.wind)).mul(0.5).add(0.5);
    return inside.mul(h).mul(float(1).sub(u.noiseAmount).add(u.noiseAmount.mul(n)));
  };
  // v3's light taps read the MEAN medium (the noise at its mean, 0.5): the same optical depth toward the light on
  // average without two more MaterialX noise evaluations per light per step (THE TAP LAW — measured on SwiftShader:
  // with noise taps the first frame never finished and the GPU process grew ~60 MB/s to the 6 GB watchdog in 104 s;
  // without taps the frame took 0.5 s at 220 MB).
  const tapDensityAt = (p: unknown): unknown => {
    const pp = vec3(p as never);
    const edge = float(1.5);
    const inX = pp.x.sub(u.boxMin.x).min(u.boxMax.x.sub(pp.x)).div(edge).clamp(0, 1);
    const inY = pp.y.sub(u.boxMin.y).min(u.boxMax.y.sub(pp.y)).div(edge).clamp(0, 1);
    const inZ = pp.z.sub(u.boxMin.z).min(u.boxMax.z.sub(pp.z)).div(edge).clamp(0, 1);
    const h = pp.y.sub(u.boxMin.y).max(0).div(u.heightFalloff).negate().exp();
    return inX.mul(inY).mul(inZ).mul(h).mul(float(1).sub(u.noiseAmount.mul(0.5)));
  };
  const volCommon = {
    density: u.density, albedo: u.albedo, ambient: u.ambient, color: u.color,
    boxMin: u.boxMin, boxMax: u.boxMax, steps: u.steps, useSceneDepth: config?.useSceneDepth ?? true,
    sunCloudLit, lightTaps: config?.lightTaps, emission: config?.emissionNode, tapDensityAt,
  };
  const geometry = new THREE.BoxGeometry(size[0], size[1], size[2]);
  const resolution: VolumeResolution = config?.resolution === 'half' ? 'half' : 'full';
  const composite: VolumeComposite = resolution === 'half' ? 'correct' : (config?.composite === 'correct' ? 'correct' : 'legacy');
  const slots = composite === 'correct' ? Math.max(0, Math.min(MAX_SPLIT_BOXES, Math.floor(config?.splitBoxes ?? 0))) : 0;
  const rectSamples: 0 | 1 | 4 = composite === 'correct' ? (config?.rectSamples ?? 0) : 0;
  const frontOrder = config?.frontRenderOrder ?? 9.5;
  const split = slots > 0 ? {
    mins: Array.from({ length: slots }, () => uniform(new THREE.Vector3(1e9, 1e9, 1e9))),
    maxs: Array.from({ length: slots }, () => uniform(new THREE.Vector3(1e9, 1e9, 1e9))),
    count: u.splitCount,
  } : undefined;
  const stats: ShadowedLightVolumeStats = { mode: `${resolution}/${composite}${slots ? `/split${slots}` : ''}${rectSamples ? `/rect${rectSamples}` : ''}`, half: [0, 0], marches: 0, skipped: 0, splitActive: 0 };
  const newMaterial = (opts: VolumeModelOptions, name: string): ShadowedVolumeMaterial => {
    const m = new ShadowedVolumeMaterial();
    m.name = name;
    m.model = new ShadowedVolumeLightingModel({ g: u.g }, densityAt, volCommon, opts);
    m.steps = config?.steps ?? 24;
    return m;
  };

  // ── v2 (legacy) — unchanged ──
  if (composite === 'legacy') {
    const material = newMaterial({ kind: 'legacy', layer: 'all', rectSamples: 0 }, 'ShadowedLightVolume');
    // Per-pixel, per-frame jitter of the first step (banding → noise TAA/blur can take).
    material.offsetNode = hash(screenUV.x.add(screenUV.y.mul(7.13)).mul(4096).add(time.fract().mul(31)));
    material.premultipliedAlpha = true;
    material.blending = THREE.NormalBlending;
    material.transparent = true;
    material.depthWrite = false;
    material.depthTest = false;
    material.side = THREE.BackSide;
    // WHY (shader sweep 2026-10-06): the v3 composite was marked, this v2 path ('legacy', opt-in) was not — depthTest
    // off, it rewrote the normal / velocity targets over the whole box (THE OVERLAY LAW).
    markMrtOverlay(material);

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'shadowed-light-volume';
    // AnalyticLightNode only sets a light's shadow up for objects that RECEIVE
    // shadows — without this the sun reaches the march unshadowed.
    mesh.receiveShadow = true;
    mesh.castShadow = false;
    mesh.renderOrder = 1; // before the additive sprites (projectiles sit at 15)
    mesh.frustumCulled = false; // the camera is usually INSIDE the box

    return {
      mesh, material,
      /** v2: `sunDir` (world, toward the sun) exists when `cloudShadow` was configured — drive it per frame. */
      uniforms: u as unknown as ShadowedLightVolumeUniforms & { sunDir?: { value: THREE.Vector3 } },
      setBoxWorld(center, half) {
        (u.boxMin.value as THREE.Vector3).set(center.x - half.x, center.y - half.y, center.z - half.z);
        (u.boxMax.value as THREE.Vector3).set(center.x + half.x, center.y + half.y, center.z + half.z);
      },
      setSplitBoxes() { /* legacy: no split */ },
      attach() { /* legacy: nothing to bind */ },
      stats,
      dispose() { geometry.dispose(); material.dispose(); },
    };
  }

  // ── v3 ──
  const jitter = (): N => jitterNode(u.jitterMode, 1);
  const setBoxUniforms = (center: THREE.Vector3, half: THREE.Vector3): void => {
    (u.boxMin.value as THREE.Vector3).set(center.x - half.x, center.y - half.y, center.z - half.z);
    (u.boxMax.value as THREE.Vector3).set(center.x + half.x, center.y + half.y, center.z + half.z);
  };
  const setSplit = (boxes: readonly VolumeSplitBox[]): void => {
    if (!split) return;
    const n = Math.min(boxes.length, split.mins.length);
    for (let k = 0; k < split.mins.length; k++) {
      const b = boxes[k];
      if (k < n && b) {
        (split.mins[k].value as THREE.Vector3).set(b.center.x - b.half.x, b.center.y - b.half.y, b.center.z - b.half.z);
        (split.maxs[k].value as THREE.Vector3).set(b.center.x + b.half.x, b.center.y + b.half.y, b.center.z + b.half.z);
      } else {
        (split.mins[k].value as THREE.Vector3).set(1e9, 1e9, 1e9);
        (split.maxs[k].value as THREE.Vector3).set(1e9, 1e9, 1e9);
      }
    }
    (u.splitCount as unknown as { value: number }).value = n;
    stats.splitActive = n;
  };

  if (resolution === 'full') {
    // One mesh ('all') or a back mesh + a front child (split).
    const backMat = newMaterial({ kind: 'march', layer: slots ? 'back' : 'all', split, rectSamples }, 'ShadowedLightVolume');
    backMat.offsetNode = jitter();
    applyCorrectComposite(backMat);
    const mesh = new THREE.Mesh(geometry, backMat);
    mesh.name = 'shadowed-light-volume';
    mesh.receiveShadow = true; mesh.castShadow = false; mesh.renderOrder = 1; mesh.frustumCulled = false;
    let frontMesh: THREE.Mesh | null = null;
    if (slots) {
      const frontMat = newMaterial({ kind: 'march', layer: 'front', split, rectSamples }, 'ShadowedLightVolumeFront');
      frontMat.offsetNode = jitter();
      applyCorrectComposite(frontMat);
      frontMesh = new THREE.Mesh(geometry, frontMat);
      frontMesh.name = 'shadowed-light-volume-front';
      frontMesh.receiveShadow = true; frontMesh.castShadow = false; frontMesh.renderOrder = frontOrder; frontMesh.frustumCulled = false;
      frontMesh.visible = false;
      mesh.add(frontMesh);
    }
    return {
      mesh, material: backMat,
      uniforms: u as unknown as ShadowedLightVolumeUniforms & { sunDir?: { value: THREE.Vector3 } },
      setBoxWorld: setBoxUniforms,
      setSplitBoxes(boxes) { setSplit(boxes); if (frontMesh) frontMesh.visible = stats.splitActive > 0; },
      attach() { /* full: the meshes use the scene's own lights */ },
      stats,
      dispose() { geometry.dispose(); backMat.dispose(); (frontMesh?.material as THREE.Material | undefined)?.dispose(); },
    };
  }

  // ── v3 HALF RESOLUTION ──
  const fallback: VolumeEdgeFallback = config?.edgeFallback === 'nearest' ? 'nearest' : 'march';
  const halfBack = new THREE.RenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false, stencilBuffer: false });
  const halfFront = new THREE.RenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: false, stencilBuffer: false });
  for (const rt of [halfBack, halfFront]) {
    rt.texture.minFilter = THREE.NearestFilter; rt.texture.magFilter = THREE.NearestFilter; rt.texture.generateMipmaps = false;
    rt.texture.colorSpace = THREE.NoColorSpace;
  }
  halfBack.texture.name = 'volume-half-back'; halfFront.texture.name = 'volume-half-front';
  // The copy of the scene pass's depth, taken right before the half march (ViewportDepthTextureNode's own recipe) …
  const depthCopy = new THREE.DepthTexture(1, 1);
  depthCopy.name = 'volume-depth-copy';
  // … and the depth the march READS (THE QUEUE-ORDER LAW): three's WebGPU backend records the framebuffer copy in the
  // OUTER pass's command encoder (submitted when the scene pass ends) while the nested march is its own encoder,
  // submitted at once — so on WebGPU the march executes BEFORE this frame's copy (measured: a fresh volume's first
  // half frame marched an empty copy, depth 0 → no volume; the fire's front haze and the window's rect term missing on
  // that frame). On WebGPU `copyTextureToTexture(copy → used)` right after the copy is submitted immediately: it runs
  // before the outer copy (used = last frame's depth); on WebGL 2 everything runs in call order (used = this frame's,
  // taken by a second framebuffer copy). The march
  // reads `used`; the composite judges each coarse texel by `used` against the pixel's own `copy` — a surface that
  // moved since is rejected and marched at full resolution, so the one-frame lag never reaches the picture.
  const depthUsed = new THREE.DepthTexture(1, 1);
  depthUsed.name = 'volume-depth-used';
  const fullSize = uniform(new THREE.Vector2(1, 1));
  const halfSize = uniform(new THREE.Vector2(1, 1));
  const layerU = uniform(0);
  const skipU = uniform(0);
  // The representative full texel of a half pixel: (2i + 1, 2j + 1), clamped — the composite re-reads the same one.
  const repDepth = (): N => texture(depthUsed).load(ivec2(floor(screenCoordinate.xy).mul(2).add(1)).clamp(ivec2(0), ivec2(vec2(fullSize).sub(1)))).r;
  const ownDepth = (): N => texture(depthCopy).load(ivec2(floor(screenCoordinate.xy)).clamp(ivec2(0), ivec2(vec2(fullSize).sub(1)))).r;

  // The half march: a private scene, the MAIN scene's lights (attach), raw (C, T), replace.
  const marchMat = newMaterial({ kind: 'march', layer: 'uniform', layerUniform: layerU, depthAt: repDepth, split, rectSamples }, 'ShadowedLightVolumeHalfMarch');
  marchMat.offsetNode = jitter();
  marchMat.volumeOutput = 'raw';
  marchMat.premultipliedAlpha = false;
  marchMat.fog = false;
  marchMat.blending = THREE.NoBlending;
  marchMat.transparent = false;
  marchMat.depthWrite = false; marchMat.depthTest = false; marchMat.side = THREE.BackSide;
  const marchMesh = new THREE.Mesh(geometry, marchMat);
  marchMesh.name = 'shadowed-light-volume-half-march';
  marchMesh.receiveShadow = true; marchMesh.castShadow = false; marchMesh.frustumCulled = false;
  const marchScene = new THREE.Scene();
  marchScene.name = 'ShadowedLightVolume [half]';
  marchScene.add(marchMesh);

  const compositeMat = (layer: 'back' | 'front' | 'all', tex: THREE.Texture, fb: VolumeEdgeFallback, name: string): ShadowedVolumeMaterial => {
    const m = newMaterial({
      kind: 'composite', layer, depthAt: ownDepth, split, rectSamples,
      upsample: { half: tex, depth: depthCopy, depthUsed, fullSize, halfSize, tolerance: u.edgeTolerance, fallback: fb, debug: u.debugView, skip: skipU },
    }, name);
    m.offsetNode = jitter();
    applyCorrectComposite(m);
    return m;
  };
  const backComp = compositeMat(slots ? 'back' : 'all', halfBack.texture, fallback, 'ShadowedLightVolumeComposite');
  const mesh = new THREE.Mesh(geometry, backComp);
  mesh.name = 'shadowed-light-volume';
  mesh.receiveShadow = true; mesh.castShadow = false; mesh.renderOrder = 1; mesh.frustumCulled = false;
  let frontMesh: THREE.Mesh | null = null;
  if (slots) {
    // The front layer marches its misses at full resolution too: with 'nearest' a pixel whose coarse texels were all
    // rejected (a disocclusion, a fresh volume's first frame on WebGPU — THE QUEUE-ORDER LAW) took a stale sample and
    // the fire showed through undimmed for that frame (measured: 4.26 against the full split's 2.94).
    frontMesh = new THREE.Mesh(geometry, compositeMat('front', halfFront.texture, 'march', 'ShadowedLightVolumeCompositeFront'));
    frontMesh.name = 'shadowed-light-volume-front';
    frontMesh.receiveShadow = true; frontMesh.castShadow = false; frontMesh.renderOrder = frontOrder; frontMesh.frustumCulled = false;
    frontMesh.visible = false;
    mesh.add(frontMesh);
  }

  let rendererRef: THREE.WebGPURenderer | null = null;
  let primary: THREE.Camera | null = null;
  const prevClear = new THREE.Color();
  // The borrowed lights' identity (ids + castShadow). The march's render object is keyed by the PRIVATE scene's lights
  // node, so three never notices a light added, removed or losing its shadow — the pipeline would keep a disposed
  // ShadowNode (measured: `shadowMap` null in updateShadow the frame after `castShadow = false`). A change rebuilds it.
  let lightsKey: number | null = null;
  // THE WARM (coordinator, 2026-09-30): the march scene is PRIVATE — no compileAsync sweep of the main scene reaches it
  // (and the nested render is skipped inside one) — so its first draw compiled its heavy pipeline SYNCHRONOUSLY inside
  // the scene pass (207 s under SwiftShader with the world's 17 lights; a visible hitch on a real GPU). The first
  // primary frame queues `compileAsync(marchScene)` against the half target (the pipeline key carries the target's
  // format and the MRT, both captured synchronously at the call) on a microtask — never inside a render — and the
  // volume draws nothing until it resolves. A change of the borrowed lights (a rebuild) warms again.
  let warm: 'cold' | 'compiling' | 'ready' = 'cold';
  let warmWaiters: Array<() => void> = [];
  const setWarm = (): void => { warm = 'ready'; const w = warmWaiters; warmWaiters = []; for (const f of w) f(); };
  const isWarm = (): boolean => warm === 'ready'; // (a closure read: setWarm() above may have run)

  // THE NESTED HALF MARCH — fired right before the back composite draws (inside the scene pass, after the opaques).
  mesh.onBeforeRender = (renderer: any, _scene: unknown, camera: THREE.Camera) => {
    const r = renderer as N;
    if (r._compilationPromises) return; // a compileAsync sweep calls onBeforeRender too — never render inside it
    const target = r.getRenderTarget() as THREE.RenderTarget | null;
    const ok = !!target && !!rendererRef && (primary ? camera === primary : !(camera as { isArrayCamera?: boolean }).isArrayCamera);
    (skipU as unknown as { value: number }).value = ok ? 0 : 1;
    if (!ok || !target) { stats.skipped++; return; }
    const W = target.width, H = target.height, Wh = Math.max(1, Math.ceil(W / 2)), Hh = Math.max(1, Math.ceil(H / 2));
    // The copy must match the source's depth format (WebGPU refuses a mismatched copyTextureToTexture; three's pass
    // depth is UnsignedInt — depth24plus — and FloatType under reversed depth).
    const src = target.depthTexture;
    for (const d of [depthCopy, depthUsed]) {
      if (src && (d.type !== src.type || d.format !== src.format)) { d.type = src.type; d.format = src.format; d.needsUpdate = true; }
      if (d.image.width !== W || d.image.height !== H) { d.image.width = W; d.image.height = H; d.needsUpdate = true; }
    }
    (fullSize.value as THREE.Vector2).set(W, H);
    (halfSize.value as THREE.Vector2).set(Wh, Hh);
    if (halfBack.width !== Wh || halfBack.height !== Hh) { halfBack.setSize(Wh, Hh); halfFront.setSize(Wh, Hh); }
    stats.half = [Wh, Hh];
    const owner = (marchMat as unknown as { lightsNode: N }).lightsNode;
    const key = owner ? Number(owner.customCacheKey()) : 0;
    if (lightsKey !== null && key !== lightsKey) { marchMat.needsUpdate = true; warm = 'cold'; }
    lightsKey = key;
    if (warm !== 'ready') {
      if (warm === 'cold' && typeof r.compileAsync === 'function') {
        warm = 'compiling';
        stats.warms = (stats.warms ?? 0) + 1;
        const cam = camera;
        queueMicrotask(() => {
          let p: Promise<unknown>;
          const prevT = r.getRenderTarget(), prevMrt = r.getMRT();
          try {
            r.setMRT(null);
            r.setRenderTarget(halfBack);
            p = Promise.resolve(r.compileAsync(marchScene, cam));
          } catch (e) { p = Promise.reject(e); } finally { r.setRenderTarget(prevT); r.setMRT(prevMrt); }
          // a failed warm never holds the volume off: the next draw compiles as it always did
          p.then(setWarm, setWarm);
        });
      } else if (typeof r.compileAsync !== 'function') setWarm();
      if (!isWarm()) { (skipU as unknown as { value: number }).value = 1; stats.skipped++; return; }
    }
    r.copyFramebufferToTexture(depthCopy);
    // WebGPU: an immediate submit (runs before the outer copy — last frame's depth). WebGL 2: three's depth
    // texture-to-texture copy needs both textures attached to render targets, so a second framebuffer copy (in order —
    // this frame's depth, the same as `copy`).
    if ((r.backend as { isWebGPUBackend?: boolean }).isWebGPUBackend) r.copyTextureToTexture(depthCopy, depthUsed);
    else r.copyFramebufferToTexture(depthUsed);
    const cubeFace = r.getActiveCubeFace(), mip = r.getActiveMipmapLevel();
    const mrt = r.getMRT(), autoClear = r.autoClear, clearAlpha = r.getClearAlpha();
    r.getClearColor(prevClear);
    r.setMRT(null);
    r.autoClear = true;
    r.setClearColor(0x000000, 1); // (C 0, T 1) where the box does not cover
    (layerU as unknown as { value: number }).value = 0;
    r.setRenderTarget(halfBack);
    r.render(marchScene, camera);
    stats.marches++;
    if (stats.splitActive > 0) {
      (layerU as unknown as { value: number }).value = 1;
      r.setRenderTarget(halfFront);
      r.render(marchScene, camera);
      stats.marches++;
    }
    r.setClearColor(prevClear, clearAlpha);
    r.autoClear = autoClear;
    r.setMRT(mrt);
    r.setRenderTarget(target, cubeFace, mip);
  };

  return {
    mesh, material: backComp,
    uniforms: u as unknown as ShadowedLightVolumeUniforms & { sunDir?: { value: THREE.Vector3 } },
    setBoxWorld(center, half) {
      setBoxUniforms(center, half);
      // The private scene carries no rebase: the march box sits at the render-space centre, scaled to the half extents.
      marchMesh.position.copy(center);
      marchMesh.scale.set((2 * half.x) / size[0], (2 * half.y) / size[1], (2 * half.z) / size[2]);
    },
    setSplitBoxes(boxes) { setSplit(boxes); if (frontMesh) frontMesh.visible = stats.splitActive > 0; },
    whenWarm() { return warm === 'ready' ? Promise.resolve() : new Promise<void>((res) => { warmWaiters.push(res); }); },
    attach(renderer, scene, camera) {
      rendererRef = renderer;
      primary = camera ?? null;
      // THE MAIN SCENE'S LIGHTS in the nested march: three's lights node for that scene holds this frame's lights
      // WHILE the scene renders (Lighting.beginRender / RenderList.finish), which is exactly when the march runs.
      (marchMat as unknown as { lightsNode: N }).lightsNode = (renderer as unknown as { lighting: { getNode(s: THREE.Scene): N } }).lighting.getNode(scene);
      marchMat.needsUpdate = true;
    },
    stats,
    dispose() {
      geometry.dispose(); marchMat.dispose(); backComp.dispose(); (frontMesh?.material as THREE.Material | undefined)?.dispose();
      halfBack.dispose(); halfFront.dispose(); depthCopy.dispose(); depthUsed.dispose();
      mesh.onBeforeRender = () => { /* disposed */ };
      setWarm(); // never leave a waiter hanging on a disposed volume
    },
  };
}
